import './App.css';
import React, { useEffect, useState } from 'react';
import { createBrowserRouter, createRoutesFromElements, Link, Navigate, Outlet, Route, RouterProvider, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import NavContainer from './components/nav_container';
import { Home } from './components/home';
import { District } from './components/district';
import { Footer } from './components/footer';
import { Location } from './components/location';
import { Schedule } from './components/schedule';
import { SchedulesHomepage } from './components/schedules_homepage';
import { LocationsHomepage } from './components/locations_homepage';
import { CreateServiceTicket } from './components/create_service_ticket';
import { ServiceTickets } from './components/service_tickets';
import { Login } from './components/login';
import { ProtectedRoute } from './components/protected_route';
import { ServiceTicket } from './components/service_ticket';
import { Notifications } from './components/notifications';
import { NotificationOpen } from './components/notification_open';
import { Profile } from './components/profile';
import { AdminSettings } from './components/admin_settings';
import { Invoices } from './components/invoices';

export const Root = ({ signedIn, currentUser }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isLoginPage = location.pathname === '/login';
  const isCreateTicketPage = location.pathname === '/createTicket';
  const showPortalChrome = signedIn && !isLoginPage;
  const showSchedules = currentUser?.isAdmin || currentUser?.portalApp !== 'fire_pest';
  const section = location.pathname.split('/').filter(Boolean)[0] || 'dashboard';
  const titles = {
    dashboard: 'Dashboard', locations: 'Locations', location: 'Location details',
    serviceTickets: 'Service Requests', serviceTicket: 'Service Request', createTicket: 'New Service Request',
    Schedules: 'Schedules', schedule: 'Schedule details', invoices: 'Invoices', notifications: 'Notifications',
    profile: 'Profile', settings: 'Settings', district: 'District details'
  };
  return (
    <div className={`appShell ${showPortalChrome ? '' : 'appShellGuest'} ${collapsed ? 'appShellCollapsed' : ''}`}>
      {showPortalChrome && <NavContainer
        collapsed={collapsed}
        onToggleCollapsed={() => setCollapsed(value => !value)}
        mobileOpen={mobileOpen}
        onMobileOpen={() => setMobileOpen(true)}
        onMobileClose={() => setMobileOpen(false)}
      />}
      <div className="appContent">
        {showPortalChrome && (
          <header className="portalTopbar">
            <h1>{titles[section] || 'Client Portal'}</h1>
            <div className="portalTopbarActions">
              {!isCreateTicketPage && <Link className="topbarButton" to="/createTicket">+ New Service Request</Link>}
              {showSchedules && section !== 'dashboard' && <Link className="topbarButton topbarButtonSecondary" to="/Schedules">View Schedules</Link>}
            </div>
          </header>
        )}
        <main className="mainContent"><Outlet /></main>
        {showPortalChrome && <Footer />}
      </div>
    </div>
  );
};

const Protected = ({ signedIn, children }) => <ProtectedRoute isSignedIn={signedIn} path="/login">{children}</ProtectedRoute>;

const App = ({ currentUser }) => {
  const dispatch = useDispatch();
  const storedUser = (() => {
    try { return JSON.parse(sessionStorage.getItem('currentUser')); } catch (error) { return null; }
  })();
  const signedIn = Boolean(currentUser?.userId || storedUser?.userId);
  const isAdmin = Boolean(currentUser?.isAdmin ?? storedUser?.isAdmin);
  const effectiveUser = currentUser?.userId ? currentUser : storedUser;
  const showSchedules = isAdmin || effectiveUser?.portalApp !== 'fire_pest';

  useEffect(() => {
    let cancelled = false;
    fetch('/apiloggedIn')
      .then(response => response.json())
      .then(user => {
        if (cancelled) return;
        if (user?.userId) {
          dispatch({ type: 'LOG_IN', payload: user });
          sessionStorage.setItem('currentUser', JSON.stringify(user));
        } else {
          dispatch({ type: 'LOG_OUT', payload: null });
          sessionStorage.removeItem('currentUser');
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [dispatch]);

  const protect = element => <Protected signedIn={signedIn}>{element}</Protected>;
  const protectAdmin = element => protect(isAdmin ? element : <Navigate to="/" replace />);
  const router = createBrowserRouter(createRoutesFromElements(
    <Route path="/" element={<Root signedIn={signedIn} currentUser={effectiveUser} />}>
      <Route index element={protect(<Home />)} />
      <Route path="district/:id" element={protect(<District />)} />
      <Route path="location/:id" element={protect(<Location />)} />
      <Route path="schedule/:id" element={protect(showSchedules ? <Schedule /> : <Navigate to="/" replace />)} />
      <Route path="Schedules" element={protect(showSchedules ? <SchedulesHomepage /> : <Navigate to="/" replace />)} />
      <Route path="locations" element={protect(<LocationsHomepage />)} />
      <Route path="createTicket" element={protect(<CreateServiceTicket />)} />
      <Route path="serviceTickets" element={protect(<ServiceTickets />)} />
      <Route path="serviceTicket/:id" element={protect(<ServiceTicket />)} />
      <Route path="invoices" element={protect(<Invoices />)} />
      <Route path="notifications" element={protect(<Notifications />)} />
      <Route path="notifications/:id/open" element={protect(<NotificationOpen />)} />
      <Route path="profile" element={protect(<Profile />)} />
      <Route path="settings" element={protectAdmin(<AdminSettings />)} />
      <Route path="login" element={<Login />} />
    </Route>
  ));

  return <div className="App"><RouterProvider router={router} /></div>;
};

export default App;
